import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const RealtimeContext = createContext(null);

const socketUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/ws`;
};

/**
 * One shared socket for the whole app: live case changes, who is online and who is typing in
 * which field. Nothing here ever blocks an edit — it only tells people what others are doing.
 */
export const RealtimeProvider = ({ children }) => {
  const socket = useRef(null);
  const attempts = useRef(0);
  const disposed = useRef(false);
  const unauthorized = useRef(false);
  const handlers = useRef(new Set());
  const heartbeat = useRef(null);
  const retryTimer = useRef(null);

  const [connected, setConnected] = useState(false);
  const [me, setMe] = useState(null);
  const [online, setOnline] = useState([]);
  const [editing, setEditing] = useState({}); // `${tin}|${field}` -> entry
  const myEmail = useRef(null);

  // Your own "editing" pings are dropped: re-rendering the form you are typing in used to close
  // native date pickers on the first click.
  const applyEditing = useCallback((entries) => {
    const next = {};
    (entries || []).forEach((entry) => {
      if (entry.email && entry.email === myEmail.current) return;
      next[`${entry.case_tin}|${entry.field_id}`] = entry;
    });
    setEditing(next);
  }, []);

  const connect = useCallback(() => {
    if (disposed.current || unauthorized.current) return;
    let ws;
    try {
      ws = new WebSocket(socketUrl());
    } catch {
      return;
    }
    socket.current = ws;

    ws.onopen = () => {
      attempts.current = 0;
      setConnected(true);
      heartbeat.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
      }, 25000);
    };

    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      if (data.type === 'welcome') {
        myEmail.current = data.me?.email || null;
        setMe(data.me);
        setOnline(data.online || []);
        applyEditing(data.editing);
        return;
      }
      if (data.type === 'presence' || data.type === 'state') {
        setOnline(data.online || []);
        if (data.type === 'state') applyEditing(data.editing);
        return;
      }
      if (data.type === 'field_editing') {
        if (data.entry?.email && data.entry.email === myEmail.current) return;
        setEditing((prev) => ({
          ...prev,
          [`${data.entry.case_tin}|${data.entry.field_id}`]: data.entry,
        }));
        return;
      }
      if (data.type === 'field_editing_stopped') {
        if (data.email && data.email === myEmail.current) return;
        setEditing((prev) => {
          const next = { ...prev };
          delete next[`${data.case_tin}|${data.field_id}`];
          return next;
        });
        return;
      }
      if (data.type === 'case_editing_cleared') {
        setEditing((prev) =>
          Object.fromEntries(
            Object.entries(prev).filter(
              ([, entry]) => !(entry.case_tin === data.case_tin && entry.email === data.email)
            )
          )
        );
        return;
      }
      if (data.type === 'case_changed') {
        setEditing((prev) =>
          Object.fromEntries(
            Object.entries(prev).filter(([, entry]) => entry.case_tin !== data.case_tin)
          )
        );
        handlers.current.forEach((handler) => handler(data));
      }
    };

    ws.onclose = (event) => {
      setConnected(false);
      if (heartbeat.current) clearInterval(heartbeat.current);
      if (event.code === 4401) {
        unauthorized.current = true; // not signed in — stop trying
        return;
      }
      if (disposed.current) return;
      attempts.current = Math.min(attempts.current + 1, 6);
      retryTimer.current = setTimeout(connect, 1000 * attempts.current);
    };

    ws.onerror = () => {};
  }, [applyEditing]);

  useEffect(() => {
    disposed.current = false;
    connect();
    return () => {
      disposed.current = true;
      if (heartbeat.current) clearInterval(heartbeat.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
      if (socket.current) socket.current.close();
    };
  }, [connect]);

  const send = useCallback((message) => {
    const ws = socket.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(message));
  }, []);

  const value = useMemo(
    () => ({
      connected,
      me,
      online,
      editing,
      send,
      startEditing: (caseTin, fieldId, fieldLabel) =>
        send({ type: 'field_edit_start', case_tin: caseTin, field_id: fieldId, field_label: fieldLabel }),
      stopEditing: (caseTin, fieldId) =>
        send({ type: 'field_edit_end', case_tin: caseTin, field_id: fieldId }),
      closeCase: (caseTin) => send({ type: 'case_closed', case_tin: caseTin }),
      notifyCaseChanged: (payload) => send({ type: 'case_changed', ...payload }),
      // Only other people are shown — never your own cursor
      editorsFor: (caseTin) =>
        Object.values(editing).filter(
          (entry) => entry.case_tin === caseTin && entry.email !== me?.email
        ),
      editorOf: (caseTin, fieldId) => {
        const entry = editing[`${caseTin}|${fieldId}`];
        return entry && entry.email !== me?.email ? entry : null;
      },
      onCaseChange: (handler) => {
        handlers.current.add(handler);
        return () => handlers.current.delete(handler);
      },
    }),
    [connected, me, online, editing, send]
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};

const NOOP_REALTIME = {
  connected: false,
  me: null,
  online: [],
  editing: {},
  send: () => {},
  startEditing: () => {},
  stopEditing: () => {},
  closeCase: () => {},
  notifyCaseChanged: () => {},
  editorsFor: () => [],
  editorOf: () => null,
  onCaseChange: () => () => {},
};

export const useRealtime = () => useContext(RealtimeContext) || NOOP_REALTIME;

export default RealtimeProvider;
